import { Controller, type UseFormReturn } from "react-hook-form";
import { Zap } from "lucide-react";
import type { CreateProductionOrderRequest } from "@opus-dt/shared-types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Notify } from "@/components/ui/notify";
import { DateTimeField } from "@/components/ui/datetime-field";
import { useLines } from "@/features/lines/hooks";
import { useProducts } from "@/features/products/hooks";
import { ProductsPrimaryField } from "./ProductsPrimaryField";
import { ProductionOrderNotesFields } from "./ProductionOrderNotesFields";
import { OpLinePreview } from "./OpLinePreview";
import { OpExpedienteNotice } from "./OpExpedienteNotice";
import { DueDateField } from "./DueDateField";
import { OrderCharacteristicsFields } from "./OrderCharacteristicsFields";
import { durationLabel } from "./production-order-utils";
import type { ProductionOrder } from "../../types/production-orders.types.js";

export function FieldError({ msg }: { msg?: string }) {
  return msg ? (
    <p className="text-xs text-rd-danger-700" role="alert">
      {msg}
    </p>
  ) : null;
}

// Duração planejada exibida = durationLabel da lista ("Xh YYm" | "—"); janela vazia/inválida → "—".
function planDuration(start?: string, end?: string): string {
  if (!start || !end) return "—";
  return durationLabel(start, end);
}

interface Props {
  form: UseFormReturn<CreateProductionOrderRequest>;
  conflict: boolean;
  order?: ProductionOrder;
  /** Quando aberto a partir do detalhe de uma linha: trava a linha (valor vem dos defaultValues). */
  lockedLineId?: string;
}

// Campos do formulário de OP (extraídos do orquestrador p/ manter ambos ≤200 LOC). Busca linhas/
// produtos do cache (TanStack) e edita o form via setValue — o submit/estado de conflito ficam no pai.
export function ProductionOrderFormBody({ form, conflict, order, lockedLineId }: Props) {
  const linesQuery = useLines();
  const productsQuery = useProducts();
  const { data: lines = [] } = linesQuery;
  const { data: products = [] } = productsQuery;
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = form;
  const productIds = watch("productIds") ?? [];
  const primaryProductId = watch("primaryProductId") ?? "";
  const lineId = watch("lineId");

  // Selects do form (linha/produto) não podem ficar silenciosamente vazios em erro.
  if (linesQuery.isError) {
    return <Notify.QueryError error={linesQuery.error} onRetry={() => void linesQuery.refetch()} />;
  }
  if (productsQuery.isError) {
    return (
      <Notify.QueryError error={productsQuery.error} onRetry={() => void productsQuery.refetch()} />
    );
  }

  function changeProducts(next: string[]) {
    setValue("productIds", next, { shouldValidate: true });
    if (!next.includes(primaryProductId))
      setValue("primaryProductId", next[0] ?? "", { shouldValidate: true });
  }

  return (
    <>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="po-line">Linha</Label>
          {lockedLineId ? (
            // Linha travada (origem: detalhe da linha) — o valor já vem dos defaultValues, então
            // não registramos o campo; exibimos só leitura. RHF mantém lineId no submit via defaults.
            <div
              id="po-line"
              data-testid="po-line-locked"
              className="flex h-10 items-center rounded-md border bg-bg-muted-rd px-3 text-sm text-text-secondary"
            >
              {lines.find((l) => l.id === lockedLineId)?.name ?? "—"}
            </div>
          ) : (
            <select
              id="po-line"
              {...register("lineId")}
              className="h-10 w-full rounded-md border bg-card px-3 text-sm"
            >
              <option value="">Selecione…</option>
              {lines.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          )}
          <FieldError msg={errors.lineId?.message} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="po-code">
            Código <span className="text-[11px] font-normal text-text-tertiary">· opcional</span>
          </Label>
          <Input id="po-code" placeholder="OP-AAAA-NNN" {...register("code")} />
        </div>
      </div>

      <Controller
        control={control}
        name="productIds"
        render={() => (
          <ProductsPrimaryField
            products={products}
            value={productIds}
            primaryId={primaryProductId}
            onChangeProducts={changeProducts}
            onChangePrimary={(id) => setValue("primaryProductId", id, { shouldValidate: true })}
          />
        )}
      />
      <FieldError msg={errors.productIds?.message ?? errors.primaryProductId?.message} />

      <div className="grid gap-3 md:grid-cols-3">
        <Controller
          control={control}
          name="plannedStartAt"
          render={({ field }) => (
            <DateTimeField
              id="po-start"
              label="Início planejado"
              value={field.value || null}
              onChange={(v) => field.onChange(v ?? "")}
              invalid={!!errors.plannedStartAt || conflict}
            />
          )}
        />
        <Controller
          control={control}
          name="expectedEnd"
          render={({ field }) => (
            <DateTimeField
              id="po-end"
              label="Fim planejado"
              value={field.value || null}
              onChange={(v) => field.onChange(v ?? "")}
              invalid={!!errors.expectedEnd || conflict}
            />
          )}
        />
        <div className="space-y-2">
          <Label htmlFor="po-qty">
            Quantidade <span className="text-[11px] font-normal text-text-tertiary">· un</span>
          </Label>
          <Input
            id="po-qty"
            type="number"
            min={1}
            placeholder="0"
            {...register("plannedQty", { valueAsNumber: true })}
          />
          <FieldError msg={errors.plannedQty?.message} />
        </div>
      </div>
      <FieldError msg={errors.plannedStartAt?.message ?? errors.expectedEnd?.message} />

      <DueDateField form={form} />

      <OrderCharacteristicsFields form={form} />

      {lineId ? <OpExpedienteNotice lineId={lineId} start={watch("plannedStartAt")} /> : null}

      {lineId ? (
        <OpLinePreview
          lineId={lineId}
          lineName={lines.find((l) => l.id === lineId)?.name}
          start={watch("plannedStartAt")}
          end={watch("expectedEnd")}
          excludeOrderId={order?.id}
        />
      ) : null}

      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-2">
          <Label>
            Duração planejada{" "}
            <span className="text-[11px] font-normal text-text-tertiary">· calculada</span>
          </Label>
          <div
            className="flex h-10 items-center rounded-md border bg-bg-muted-rd px-3 text-sm text-text-secondary"
            data-testid="po-duration"
          >
            {planDuration(watch("plannedStartAt"), watch("expectedEnd"))}
          </div>
        </div>
        <ProductionOrderNotesFields register={register} />
      </div>

      <div className="border-brand-500/30 flex items-start gap-2 rounded-md border bg-brand-50 p-2.5 text-xs text-brand-700">
        <Zap className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Campos compatíveis com Simulação — esta OP poderá ser simulada no futuro sem retrabalho.
        </span>
      </div>
    </>
  );
}
